import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Modal, Select } from '@arco-design/web-react';
import { produce } from 'immer';
import styled from 'styled-components';

import { SliderInput } from '@/components/SliderInput';
import { PATTERN_NAME } from '@/utils';

import { DEFAULT_QUANTITY_UNITS, useWatchedQuantityUnit } from '../../../components/quantityUnit';
import { ReclaimPolicyFormField, isReclaimPolicyLocked, resolveReclaimPolicyValue } from '../../../components/reclaimPolicyField';
import { StorageClassFormField } from '../../../components/storageClassField';
import { DISK_PRICING_FIELD, normalizeVendorDeviceMeta, readDiskPricingMeta } from '../../../components/vendorDevice';
import { OptionMetaRootField } from '../../../platform/option-meta';
import {
  type VolumeClaimTemplateItem,
  type VolumeItem,
  type VolumeMountItem,
  VolumeMountType,
  isVolumeClaimTemplateItem,
} from '../volumeTypes';
import { AddedPods } from './AddedPods';
import { resolveVolumeMountForEdit } from './resolveVolumeMount';

const modeOptions = ['ReadWriteOnce', 'ReadOnlyMany', 'ReadWriteMany'].map((item) => ({
  value: item,
  label: item,
}));

enum Field {
  STORAGE_CLASS = 'storageClass',
}

interface AddClaimTemplateModalProps {
  open?: boolean;
  onClose?: () => void;
  onOk?: (values: VolumeClaimTemplateItem, modalFormValues: Record<string, unknown>) => void;
  data?: VolumeItem;
  containerName?: string;
  from?: string;
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  resourceCode?: string;
  /** SKU / 下单注入时锁定回收策略为 Delete */
  resourceEditMode?: string;
}

export function AddClaimTemplateModal({
  open,
  onClose,
  onOk,
  data,
  containerName,
  from,
  billModelId,
  deploymentTypeId,
  resourceCode,
  resourceEditMode,
}: AddClaimTemplateModalProps) {
  const { t } = useTranslation();
  const container = containerName;
  const editData = data && isVolumeClaimTemplateItem(data) ? data : undefined;
  const openInitializedRef = useRef(false);
  const reclaimLocked = isReclaimPolicyLocked(resourceEditMode);

  const initFormValues = useMemo(() => {
    const initVolumeMounts: VolumeMountItem[] = [
      {
        containerName: container ?? '',
        mountType: VolumeMountType.NotMount,
        mountPath: '',
      },
    ];

    const initialValues: VolumeClaimTemplateItem = {
      type: 'VolumeClaimTemplate',
      volumeName: undefined,
      storageClass: undefined,
      accessModes: ['ReadWriteOnce'],
      volumeCapacity: 1,
      volumeMounts: initVolumeMounts,
      reclaimPolicy: resolveReclaimPolicyValue(undefined, reclaimLocked),
    };

    if (editData) {
      return produce(editData, (draft) => {
        draft.reclaimPolicy = resolveReclaimPolicyValue(draft.reclaimPolicy, reclaimLocked);
        const existItem = resolveVolumeMountForEdit(draft.volumeMounts, container);
        draft.volumeMounts = [
          {
            containerName: (container ?? '') || existItem?.containerName || '',
            mountType: existItem?.mountType ?? VolumeMountType.NotMount,
            mountPath: existItem?.mountPath ?? '',
          },
        ];
      });
    }

    return initialValues;
  }, [container, editData, reclaimLocked]);

  const [allValues, setAllValues] = useState<Partial<VolumeClaimTemplateItem>>(initFormValues);
  const [form] = Form.useForm<VolumeClaimTemplateItem>();
  const quantityUnit = useWatchedQuantityUnit(form, billModelId ? DISK_PRICING_FIELD : undefined, DEFAULT_QUANTITY_UNITS.disk, {
    lockDisplayToDefault: true,
  });

  const handleOk = async () => {
    try {
      await form.validate();
      const formValues = form.getFieldsValue() as Record<string, unknown>;
      const pricing = normalizeVendorDeviceMeta(readDiskPricingMeta(formValues));
      const imagePresetMeta =
        editData && (editData as VolumeClaimTemplateItem & { _fromImage?: boolean })._fromImage ? { _fromImage: true as const } : {};
      const nextValues = {
        ...(allValues as VolumeClaimTemplateItem),
        ...formValues,
        ...imagePresetMeta,
        type: 'VolumeClaimTemplate' as const,
        reclaimPolicy: resolveReclaimPolicyValue(
          (formValues.reclaimPolicy as string | undefined) ?? (allValues as VolumeClaimTemplateItem).reclaimPolicy,
          reclaimLocked,
        ),
        pricing,
      };
      // container-create 声明模板不展示 mountType，挂载模式与弹窗 accessModes 对齐
      if (from === 'container-create' && nextValues.volumeMounts?.length) {
        const primaryAccessMode = nextValues.accessModes?.[0] as VolumeMountType | undefined;
        nextValues.volumeMounts = nextValues.volumeMounts.map((mount) => ({
          ...mount,
          mountType: primaryAccessMode ?? mount.mountType,
        }));
      }
      onOk?.(nextValues, form.getFieldsValue() as Record<string, unknown>);
    } catch (error) {
      console.error('Error validating form: ', error);
    }
  };

  useEffect(() => {
    if (!open) {
      openInitializedRef.current = false;
      form.resetFields();
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;

    form.setFieldsValue(initFormValues);
    if (editData?.pricing) {
      form.setFieldsValue({ [DISK_PRICING_FIELD]: editData.pricing });
    }
    setAllValues(initFormValues);
  }, [editData?.pricing, form, initFormValues, open]);

  const pvcPrefixError = t(
    'common:invalid.prefix.prefix.can.only.contain.lowercase.letters.numbers.and.hyphens.must.start.and.end.with.a.lowercase.letter.or.number.maximum.253.characters',
  );

  return (
    <StyledModal visible={open} onCancel={onClose} onOk={handleOk} title={t('data-center:shared.volume.claim.template')}>
      <Form
        form={form}
        layout="vertical"
        initialValues={initFormValues}
        onValuesChange={(_, values) => {
          setAllValues({ ...values, type: 'VolumeClaimTemplate' });
        }}
      >
        <OptionMetaRootField />
        <Form.Item
          field="volumeName"
          label={t('data-center:shared.volume.name.prefix')}
          extra={t(
            'data-center:prefix.for.shared.volume.claim.name.prefix.can.only.contain.lowercase.letters.numbers.and.hyphens.must.start.and.end.with.a.lowercase.letter.or.number.maximum.253.characters',
          )}
          rules={[
            { required: true, message: pvcPrefixError },
            { match: PATTERN_NAME, message: pvcPrefixError },
          ]}
        >
          <Input placeholder={t('data-center:shared.volume.name.prefix')} />
        </Form.Item>
        <Form.Item
          field="accessModes"
          label={t('common:access.mode')}
          rules={[{ required: true, message: t('common:this.parameter.cannot.be.empty') }]}
        >
          <Select showSearch mode="multiple" options={modeOptions} />
        </Form.Item>
        <StorageClassFormField
          form={form}
          field={Field.STORAGE_CLASS}
          enabled={Boolean(open)}
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          resourceCode={resourceCode}
          requiredMessage={t('common:this.parameter.cannot.be.empty')}
          placeholder={t('common:storage.class')}
        />
        <Form.Item
          field="volumeCapacity"
          label={t('common:volume.capacity')}
          rules={[{ required: true, message: t('common:this.parameter.cannot.be.empty') }]}
        >
          <SliderInput
            min={1}
            max={2048}
            marks={{
              1: `1${quantityUnit}`,
              512: `512${quantityUnit}`,
              1024: `1024${quantityUnit}`,
              1536: `1536${quantityUnit}`,
              2048: `2048${quantityUnit}`,
            }}
            suffix={quantityUnit}
          />
        </Form.Item>
        <AddedPods form={form} from={from} type="template" />
        <ReclaimPolicyFormField
          form={form}
          field="reclaimPolicy"
          enabled={Boolean(open)}
          lockedToDelete={reclaimLocked}
          getPopupContainer={() => document.body}
          requiredMessage={t('common:required')}
          placeholder={t('common:select.placeholder')}
          formItemStyle={{ marginTop: 16 }}
        />
      </Form>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  width: 800px;
  .arco-modal-content {
    min-height: 400px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
`;
